import type { FeedOrder, RoundFeed } from '../milhar/roundFeedView.ts';

/**
 * 大厅实时下注流的适配层。契约见 `doc/HALL_BET_FEED_CONTRACT.md`。
 *
 * <p>流有两个来源，前端必须能在两者间无感切换：
 *
 * <p><b>① `/api/v1/hall/bets`（跨玩法）</b> —— 正常来源。
 * <p><b>② Milhar 的 `roundFeed`（只有 Milhar）</b> —— 降级来源。**端点未上线或
 * 挂掉时走它**，而不是让整块社交区消失。契约 §6 要求前端改动能先于后端上线。
 *
 * <p>轮播逻辑（去重、水位线、积压追赶）全部复用 `roundFeedView` 里那套 —— 它们只
 * 依赖 `{ id, placedAt, teimosinha }` 三个字段，所以两个来源归一成同一形状之后
 * 一行播放代码都不用改。
 */

export type HallOrderUser = {
  userId: number | null;
  username: string;
  avatarDefaultId?: number | null;
  avatarThumbUrl?: string | null;
  vipLevel?: number | null;
};

export type HallOrder = {
  /** 注单 id。**播放去重只认它**（同 roundFeedView）。 */
  id: number;
  /** `milhar` | `lastdigit` | `sicbo` | `roulette`。未知值不丢弃，见 §3.1。 */
  game: string;
  /**
   * 投注类型。**Milhar 的档位靠它区分**（`milhar` / `milhar-hora`），不是靠 `game` ——
   * 两档都上报 `game='milhar'`。
   */
  tab: string;
  pick: string;
  /** 字符串。`Number.isFinite("25.00")` 是 false，声明成 number 不会有人报错。 */
  amount: string;
  currency: string;
  /** 房间玩法才有；Milhar 为 null。 */
  roomCode: string | null;
  roundNumber: number;
  placedAt: number;
  /** 恒为 false —— 续期票在服务端就被排除了（契约 §2.2）。留着是为了形状兼容轮播逻辑。 */
  teimosinha: boolean;
  user: HallOrderUser;
};

export type HallBetsResponse = {
  ok?: boolean;
  serverTime?: number;
  orders?: HallOrder[];
};

/**
 * 降级路径：把 Milhar 的买家流转成统一形状。
 *
 * <p>`tierTab` 必须传对（`milhar` / `milhar-hora`）—— 它是前端区分两个档位的唯一
 * 依据。传错不会报错，只会让 5 位档的注单在流里显示成 4 位档。
 */
export function hallOrdersFromMilhar(
  feed: RoundFeed | null | undefined,
  tierTab: string,
  currency: string,
  roundNumber: number,
): HallOrder[] {
  const orders: FeedOrder[] = feed && Array.isArray(feed.orders) ? feed.orders : [];
  return orders.map((order) => ({
    id: order.id,
    game: 'milhar',
    tab: tierTab,
    pick: order.pick,
    amount: order.amount,
    currency,
    roomCode: null,
    roundNumber,
    placedAt: order.placedAt,
    teimosinha: order.teimosinha,
    user: {
      userId: order.userId,
      username: order.username,
      avatarDefaultId: order.avatarDefaultId,
      avatarThumbUrl: order.avatarThumbUrl,
      vipLevel: order.vipLevel,
    },
  }));
}

/**
 * 接口返回里能用的那些行。
 *
 * <p>**坏行被丢弃，不是被兜底。** 一条没有 id 的注单进了播放队列就会破坏去重
 * （`unseenOrders` 认 id），于是同一条会反复播；给它编一个 id 只会把问题藏起来。
 */
export function usableHallOrders(response: HallBetsResponse | null | undefined): HallOrder[] {
  const orders = response && Array.isArray(response.orders) ? response.orders : [];
  return orders.filter((order) => (
    order
    && Number.isFinite(Number(order.id))
    && Number.isFinite(Number(order.placedAt))
    && typeof order.user?.username === 'string'
    && order.user.username.length > 0
  ));
}

/**
 * 该不该退回 Milhar-only。
 *
 * <p>**只在端点不存在时降级**（契约 §6）。5xx 或超时不降级：那是暂时故障，切换数据源
 * 会让流里的玩法组成突然变化，看起来像出了别的问题；保留上一批已播内容更好。
 */
export function shouldFallBackToMilhar(status: number | undefined): boolean {
  return status === 404 || status === 501;
}

/** 去掉两个来源之间可能重复的 Milhar 行 —— 以 id 为准，混流优先。 */
export function mergeById(primary: HallOrder[], fallback: HallOrder[]): HallOrder[] {
  if (primary.length === 0) return fallback;
  const seen = new Set(primary.map((order) => order.id));
  return primary.concat(fallback.filter((order) => !seen.has(order.id)));
}

/**
 * The number the lobby's "N playing right now" shows.
 *
 * `total` is the real one: distinct visitors heartbeating site-wide in the
 * last 45s (`/api/rooms/presence`). It wins whenever it is a positive number.
 * The viewer's own heartbeat makes a genuine zero impossible, so a zero or a
 * missing value means the backend predates the field or the heartbeat has not
 * landed yet — only then fall back to the older, weaker signals: Milhar's
 * bettor count this round, then how many rows are on screen. The last one is
 * the fallback that used to be the whole story, and it is capped at the
 * window size, which is why the header used to read "10" all day.
 */
export function hallOnlineCount(
  total: number | null | undefined,
  milharPeople: number | null | undefined,
  visibleRows: number,
): number {
  if (typeof total === 'number' && Number.isFinite(total) && total > 0) return Math.floor(total);
  const people = Number(milharPeople || 0);
  if (people > 0) return Math.floor(people);
  return Math.max(0, Math.floor(visibleRows));
}
